import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  Quiz,
  TrendingUp,
  Assignment,
  CheckCircle,
  Cancel,
  EmojiEvents,
  School,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import quizAPI from '../../services/quizAPI';
import api from '../../services/api';

const StudentDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentAttempts, setRecentAttempts] = useState([]);
  const [availableQuizzes, setAvailableQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStudentData();
  }, []);

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('📊 Fetching student stats...');
      
      // Récupérer les statistiques personnelles
      const statsResponse = await api.get('/user/my-stats');
      console.log('✅ My stats:', statsResponse.data);
      setStats(statsResponse.data.stats);
      
      // Récupérer les tentatives récentes
      const attemptsResponse = await api.get('/attempt/my-attempts');
      console.log('✅ My attempts:', attemptsResponse.data);
      const attempts = attemptsResponse.data.attempts || [];
      setRecentAttempts(attempts.slice(0, 5));
      
      // Récupérer les quiz disponibles
      const quizzesResponse = await quizAPI.getAll({ status: 'published' });
      console.log('✅ Available quizzes:', quizzesResponse);
      const quizzes = quizzesResponse.quizzes || quizzesResponse.data?.quizzes || [];
      setAvailableQuizzes(quizzes.slice(0, 5));
      
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Erreur lors du chargement des données';
      setError(errorMsg);
      toast.error(errorMsg);
      console.error('❌ Student dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon, color = 'primary' }) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h3" component="div" color={`${color}.main`}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ color: `${color}.main`, opacity: 0.3 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Mon Tableau de Bord
      </Typography>
      
      {/* Statistiques personnelles */}
      {stats && (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Quiz Passés"
                value={stats.totalAttempts || 0}
                subtitle={`${stats.totalQuizzesTaken || 0} quiz différents`}
                icon={<Quiz sx={{ fontSize: 40 }} />}
                color="primary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Réussites"
                value={stats.passedAttempts || 0}
                subtitle={`${stats.successRate || 0}% de réussite`}
                icon={<CheckCircle sx={{ fontSize: 40 }} />}
                color="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Score Moyen"
                value={`${stats.averageScore || 0}%`}
                icon={<TrendingUp sx={{ fontSize: 40 }} />}
                color="warning"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Temps Total"
                value={`${Math.round((stats.totalTimeSpent || 0) / 60)}h`}
                subtitle={`${stats.totalTimeSpent || 0} minutes`}
                icon={<School sx={{ fontSize: 40 }} />}
                color="info"
              />
            </Grid>
          </Grid>

          {/* Progression */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📈 Votre Progression
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Taux de réussite
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {stats.successRate || 0}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={stats.successRate || 0} 
                  sx={{ height: 10, borderRadius: 5 }}
                  color={stats.successRate >= 75 ? 'success' : stats.successRate >= 50 ? 'warning' : 'error'}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {stats.passedAttempts || 0} quiz réussis sur {stats.totalQuizzesTaken || 0}
              </Typography>
            </CardContent>
          </Card>
        </>
      )}

      {/* Quiz disponibles */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            🎯 Quiz Disponibles
          </Typography>
          {availableQuizzes.length === 0 ? (
            <Typography color="text.secondary">
              Aucun quiz disponible pour le moment.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {availableQuizzes.map((quiz) => (
                <Grid item xs={12} md={6} key={quiz._id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {quiz.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {quiz.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Chip
                          label={`${quiz.questions?.length || 0} questions`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${quiz.duration} min`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`Seuil: ${quiz.passingScore}%`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => navigate(`/quiz/${quiz._id}`)}
                      >
                        Passer le Quiz
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Tentatives récentes */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📝 Mes Dernières Tentatives
          </Typography>
          
          {recentAttempts.length === 0 ? (
            <Typography color="text.secondary">
              Vous n'avez pas encore passé de quiz.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Quiz</TableCell>
                    <TableCell align="center">Score</TableCell>
                    <TableCell align="center">Résultat</TableCell>
                    <TableCell align="center">Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentAttempts.map((attempt) => (
                    <TableRow key={attempt._id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {attempt.quiz?.title || 'Quiz'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography 
                          variant="h6" 
                          color={attempt.passed ? 'success.main' : 'error.main'}
                        >
                          {attempt.score}%
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={attempt.passed ? 'Réussi' : 'Échoué'}
                          color={attempt.passed ? 'success' : 'error'}
                          size="small"
                          icon={attempt.passed ? <CheckCircle /> : <Cancel />}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(attempt.completedAt || attempt.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => navigate(`/results/${attempt._id}`)}
                        >
                          Voir Résultats
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default StudentDashboard;

